/**
 * The distinct "Update Price" action (architecture §7.4) — never part of the
 * edit form. Rupees in the UI, integer paise over the wire, converted once
 * in the submit handler — explicitly avoiding the raw-paise-displayed-as-
 * rupees bug found in the Users module's `RefundPaymentModal.tsx`.
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import { repriceProductSchema, type RepriceProductInput } from '../schemas';
import { useRepriceProduct } from '../hooks';
import { formatPrice } from '@/src/infrastructure/lib/currency';

interface Props {
  open: boolean;
  productId: string;
  productName: string;
  currentAmountInPaise: number;
  currency: string;
  onClose: () => void;
}

export function ProductPriceEditor({ open, productId, productName, currentAmountInPaise, currency, onClose }: Props) {
  const mutation = useRepriceProduct(productId);
  const form = useForm<RepriceProductInput>({
    resolver: zodResolver(repriceProductSchema),
    defaultValues: { amountInRupees: currentAmountInPaise / 100 },
  });

  // This component stays mounted permanently (`ProductActionsDropdown` always
  // renders it, gated only by `open`), so `defaultValues` captured once at
  // mount would go stale across re-opens for a different row/price — reset
  // whenever the target product or its price changes, matching the pattern
  // `EditProductForm` already uses for the same reason.
  useEffect(() => {
    form.reset({ amountInRupees: currentAmountInPaise / 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, currentAmountInPaise, open]);

  const submit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(Math.round(values.amountInRupees * 100));
    form.reset();
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title={`Update price — ${productName}`}
      description={`Current price: ${formatPrice(currentAmountInPaise, currency)}. This creates a new version; historical purchases at the old price are unaffected.`}
      confirmLabel="Update price"
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit().catch(() => {})}
    >
      <form onSubmit={(e) => void submit(e).catch(() => {})} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            New price (₹) <span className="text-[10px] font-normal lowercase tracking-normal text-muted-foreground">(min ₹500, ADR-019 sale floor)</span>
          </label>
          <input
            type="number"
            min={500}
            step="1"
            {...form.register('amountInRupees', { valueAsNumber: true })}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
          {form.formState.errors.amountInRupees && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.amountInRupees.message}</p>
          )}
        </div>
        {mutation.error && <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>}
      </form>
    </ConfirmModal>
  );
}
